import { isEmpty } from 'lodash';
import { PropsWithChildren, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useFormContext } from '@/components/react-hook-form';
import { EntityKind, EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { HypotheticalSaleLoanFormShape } from '../../EstateWaterfallHypotheticalSaleLoanModal.types';
import { EstateWaterfallHypotheticalSaleLoanModal_EstateWaterfallFragment } from '../../graphql/EstateWaterfallHypotheticalSaleLoanModal.generated';
import { SaleLoanIllustration_EstateWaterfallFragment } from '../../Illustration/graphql/SaleLoanIllustration.generated';
import {
  SaleLoanForm_HouseholdFragment,
  useSaleLoanFormQuery,
} from '../graphql/SaleLoanForm.generated';
import {
  PossibleSellerLenderType,
  SaleLoanFormContext,
} from './SaleLoanForm.context';

interface RecipientOptionParams {
  sellerId: string;
}

function getRecipientOptions(
  household: SaleLoanForm_HouseholdFragment | null,
  { sellerId }: RecipientOptionParams
): TypeaheadSelectInputOption<string>[] {
  // just to be defensive
  if (!household) return [];
  const options: TypeaheadSelectInputOption<string>[] = [];

  (household.clientProfiles ?? []).forEach((client) => {
    options.push({
      value: client.id,
      display: client.displayName,
      groupName: 'Individuals',
      disabled: client.id === sellerId,
    });
  });

  getNodes(household.recipientEntities)
    .filter((entity) =>
      [EntityKind.IrrevocableTrust, EntityKind.SlatTrust].includes(entity.kind)
    )
    .forEach((entity) => {
      options.push({
        value: entity.id,
        display: entity.subtype.displayName,
        groupName:
          entity.stage === EntityStage.Active ? 'Entities' : 'Draft entities',
        disabled: entity.id === sellerId,
      });
    });

  // sort options to be first alphabetically by group name, then by display name
  options.sort((a, b) => {
    // this won't happen, but types allow it
    if (!a.groupName || !b.groupName) return 0;

    if (a.groupName === b.groupName) {
      return a.display.localeCompare(b.display);
    }

    return a.groupName.localeCompare(b.groupName);
  });

  return options;
}

interface SellerLenderOptionParams {
  recipientId: string;
}

function getSellerLenderOptions(
  household: SaleLoanForm_HouseholdFragment | null,
  { recipientId }: SellerLenderOptionParams
): PossibleSellerLenderType[] {
  if (!household) return [];

  const sellerIndividuals = household.sellerIndividuals
    .filter((client) => !isEmpty(client.ownedOwnershipStakes))
    .map((client) => ({
      value: client.id,
      display: client.displayName,
      disabled: client.id === recipientId,
      groupName: 'Individuals',
    }));

  const sellerEntities = getNodes(household.sellerEntities).map((entity) => ({
    value: entity.id,
    display: entity.subtype.displayName,
    groupName: 'Entities',
    disabled: entity.id === recipientId,
    taxDiscount: entity.subtype.taxDiscount,
    effectiveDiscountPercent: entity.subtype.effectiveDiscountPercent,
  }));

  return [...sellerIndividuals, ...sellerEntities];
}

interface SaleLoanFormProviderProps extends PropsWithChildren {
  waterfallId: string;
  waterfall: EstateWaterfallHypotheticalSaleLoanModal_EstateWaterfallFragment | null;
  householdId: string;
  loading: boolean;
  // saleLoanId will be null if this is a new sale loan
  saleLoanId: string | null;
  // saleLoanFundingId will be null if this is a new sale loan
  saleLoanFundingId: string | null;
  growthProfileId: string | null;
}

export function SaleLoanFormProvider({
  children,
  loading: loadingExternal,
  saleLoanId,
  saleLoanFundingId,
  householdId,
  waterfallId,
  waterfall,
}: SaleLoanFormProviderProps) {
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const sellerId = useWatch({
    control,
    name: 'sellerId',
  });
  const recipientId = useWatch({
    control,
    name: 'recipientId',
  });
  const { data, loading, error } = useSaleLoanFormQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      householdId,
    },
  });

  const { possibleRecipients, possibleSellerLenders } = useMemo(() => {
    const household = getNodes(data?.households)[0];
    const possibleRecipients = getRecipientOptions(household ?? null, {
      sellerId,
    });
    const possibleSellerLenders = getSellerLenderOptions(household ?? null, {
      recipientId,
    });

    return {
      possibleRecipients,
      possibleSellerLenders,
    };
  }, [data?.households, recipientId, sellerId]);

  const [simulatedWaterfall, setSimulatedWaterfall] =
    useState<SaleLoanIllustration_EstateWaterfallFragment | null>(null);

  const contextValue = useMemo(
    () => ({
      possibleRecipients,
      possibleSellerLenders,

      loading: loading || loadingExternal,
      saleLoanId,
      waterfallId,
      saleLoanFundingId,
      error,
      waterfall,
      simulatedWaterfall,
      setSimulatedWaterfall,
    }),
    [
      possibleRecipients,
      possibleSellerLenders,
      loading,
      loadingExternal,
      saleLoanId,
      waterfallId,
      saleLoanFundingId,
      error,
      waterfall,
      simulatedWaterfall,
    ]
  );

  return (
    <SaleLoanFormContext.Provider value={contextValue}>
      {children}
    </SaleLoanFormContext.Provider>
  );
}
